import axiosLib, { type AxiosInstance } from 'axios'
import { type PollEntity } from '../entities/poll.entity'
import { type ForumPostListItemEntity } from '../entities/forum-post.entity'
import { type PollsDataSource, type SavePollParams, type UpdatePollParams } from '../polls.datasource'

interface ClubResponse {
  club: { id: number }
}

interface StrapiJwtResponse {
  jwt: string
}

/**
 * API datasource for forum polls. Uses Clerk JWT to exchange for a Strapi
 * JWT, then calls backend-v2 (which verifies Strapi JWTs). Mirrors the
 * keyword-flagging datasource shape.
 */
export class PollsApiDataSource implements PollsDataSource {
  private clubIdCache = new Map<string, number>()
  private strapiJwt: string | null = null
  private readonly backendAxios: AxiosInstance

  constructor(
    private readonly baseUrl: string,
    private readonly clerkAxios: AxiosInstance,
    private readonly getOrganizationSlug: () => string | undefined
  ) {
    this.backendAxios = axiosLib.create()
  }

  private async getStrapiJwt(): Promise<string> {
    if (this.strapiJwt) return this.strapiJwt
    const response = await this.clerkAxios.post<StrapiJwtResponse>(`${this.baseUrl}/clerk/generate-strapi-jwt`)
    this.strapiJwt = response.data.jwt
    return this.strapiJwt
  }

  private async authHeaders(): Promise<Record<string, string>> {
    const jwt = await this.getStrapiJwt()
    return { Authorization: `Bearer ${jwt}` }
  }

  private async resolveClubId(): Promise<number> {
    const slug = this.getOrganizationSlug()
    if (!slug) throw new Error('No organization selected')
    const cached = this.clubIdCache.get(slug)
    if (cached !== undefined) return cached

    const response = await this.backendAxios.get<ClubResponse>(`${this.baseUrl}/clubs/slug/${slug}`)
    const clubId = response.data.club.id
    this.clubIdCache.set(slug, clubId)
    return clubId
  }

  private async pollUrl(postId: number): Promise<{ url: string; headers: Record<string, string> }> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    return { url: `${this.baseUrl}/clubs/${clubId}/forumPosts/${postId}/poll`, headers }
  }

  async list(): Promise<PollEntity[]> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const response = await this.backendAxios.get<{ polls: PollEntity[] }>(`${this.baseUrl}/clubs/${clubId}/polls`, {
      headers
    })
    return response.data.polls
  }

  async getByPostId(postId: number): Promise<PollEntity | undefined> {
    const { url, headers } = await this.pollUrl(postId)
    try {
      const response = await this.backendAxios.get<{ poll: PollEntity | null }>(url, {
        headers
      })
      return response.data.poll ?? undefined
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((e as any)?.response?.status === 404) return undefined
      throw e
    }
  }

  async create(params: SavePollParams): Promise<PollEntity> {
    const { url, headers } = await this.pollUrl(params.postId)
    const body = {
      question: params.question,
      closesAt: params.closesAt,
      options: params.options
    }
    const response = await this.backendAxios.post<PollEntity>(url, body, { headers })
    return response.data
  }

  async update(postId: number, params: UpdatePollParams): Promise<PollEntity> {
    const { url, headers } = await this.pollUrl(postId)
    const response = await this.backendAxios.patch<PollEntity>(url, params, { headers })
    return response.data
  }

  async close(postId: number): Promise<PollEntity> {
    const { url, headers } = await this.pollUrl(postId)
    const response = await this.backendAxios.post<PollEntity>(`${url}/close`, undefined, { headers })
    return response.data
  }

  async delete(postId: number): Promise<void> {
    const { url, headers } = await this.pollUrl(postId)
    await this.backendAxios.delete(url, { headers })
  }

  async listForumPosts(): Promise<ForumPostListItemEntity[]> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    type ForumPostResponse = { id: number; title?: string; poll?: { id: number } | null }
    type PaginatedResponse = { items?: ForumPostResponse[]; posts?: ForumPostResponse[] }
    const response = await this.backendAxios.get<ForumPostResponse[] | PaginatedResponse>(
      `${this.baseUrl}/clubs/${clubId}/forumPosts`,
      { headers, params: { page: 1, pageSize: 200 } }
    )
    const raw = Array.isArray(response.data) ? response.data : response.data.items ?? response.data.posts ?? []
    return raw.map((p) => ({
      id: p.id,
      title: p.title ?? `Post #${p.id}`,
      hasPoll: !!p.poll
    }))
  }
}
